/**
 * Single source of truth for React Query keys.
 *
 * Every key is a typed factory so call sites can't drift on shape or value type
 * (a `['tasks', '7']` string key silently misses a `['tasks', 7]` number key).
 * The returned arrays are identical to the literals previously hand-written
 * across the codebase, so producers and consumers share the same cache.
 *
 * Adopt incrementally: replacing a literal with the matching factory call is a
 * pure refactor as long as the runtime value is unchanged.
 */
export const queryKeys = {
  // Space-scoped collections
  tasks: (spaceId: number) => ['tasks', spaceId],
  archivedTasks: (spaceId: number) => ['tasks', spaceId, 'archive'],
  epics: (spaceId: number) => ['epics', spaceId],
  workspace: (spaceId: number) => ['workspace', spaceId],
  workspaceSprints: (spaceId: number) => ['workspace', spaceId, 'sprints'],
  workspaceAccess: (spaceId: number) => ['workspaceAccess', spaceId],
  githubStatusRules: (spaceId: number) => ['githubStatusRules', spaceId],
  githubSpaceRepositories: (spaceId: number) => ['githubSpaceRepositories', spaceId],
  planningSession: (spaceId: number) => ['workspacePlanningSession', spaceId],

  // Organization-scoped
  workspaces: (organizationId: number) => ['workspaces', organizationId],
  notifications: (organizationId: number) => ['notifications', organizationId],
  inbox: (organizationId: number, tab: string, filter: string) => ['inbox', organizationId, tab, filter],
  organizationInvites: (organizationId: number) => ['organizationInvites', organizationId],
  githubInstallations: (organizationId: number) => ['githubInstallations', organizationId],
  githubRepositories: (organizationId: number) => ['githubRepositories', organizationId],
  githubMappableWorkspaces: (organizationId: number) => ['githubMappableWorkspaces', organizationId],
  // Keyed by the opaque pending token, not by an organization: this read happens before
  // the user has chosen one.
  githubPendingInstallation: (token: string) => ['githubPendingInstallation', token],
  slackPendingInstallation: (token: string) => ['slackPendingInstallation', token],
  slackInstallation: (organizationId: number) => ['slackInstallation', organizationId],
  // Per organization, not per user: the server scopes this to the session's own
  // identity, and a session only ever holds one user.
  slackIdentity: (organizationId: number) => ['slackIdentity', organizationId],

  // Single-task sub-resources
  // Keyed by the space PUBLIC id (from the URL), not the numeric id: Task View starts
  // this fetch before the workspace lookup resolves the numeric one.
  taskDetail: (spacePublicId: string, taskNumber: number) => ['taskDetail', spacePublicId, taskNumber],
  // Matches every `taskDetail(...)` entry: the cross-key helpers in tasks.ts
  // (write / remove / invalidate) fan out over all of them by task id.
  taskDetailRoot: () => ['taskDetail'] as const,
  taskSubtasks: (taskId: number) => ['taskSubtasks', taskId],
  // Matches every `taskSubtasks(...)` entry. A delete event names the deleted
  // task but never the parent it hung under, so eviction fans out over all of
  // them (#3100).
  taskSubtasksRoot: () => ['taskSubtasks'] as const,
  taskLinks: (taskId: number) => ['taskLinks', taskId],
  taskComments: (taskId: number) => ['taskComments', taskId],
  taskActivity: (taskId: number) => ['taskActivity', taskId],
  taskAttachments: (taskId: number) => ['taskAttachments', taskId],
  taskDependencies: (taskId: number) => ['taskDependencies', taskId],
  taskPullRequests: (taskId: number) => ['taskPullRequests', taskId],

  // Sprints: `sprintRoot()` matches every `sprint(id)` for broad invalidation.
  sprintRoot: () => ['sprint'],
  sprint: (sprintId: number) => ['sprint', sprintId],

  // Imports
  imports: () => ['imports'],
  import: (importId: number) => ['import', importId],

  // Auth / org membership
  userFromAccessToken: () => ['userFromAccessToken'],
  organization: () => ['organization'],
};
